/**
 * Element Component Configs for Puck Editor
 * Defines Puck ComponentConfig for each granular element building block.
 * These are spread into puckConfig.components to register them as draggable elements.
 */

import type { ComponentConfig } from '@puckeditor/core'
import * as React from 'react'

import {
  columnSlotDefaultProps,
  columnSlotFields,
  getColumnSlotKeys,
  getGridSlotKeys,
  gridSlotDefaultPropsMax,
  gridSlotFields,
  LAYOUT_CONTENT_SLOT,
  LAYOUT_SLOT_MIN_HEIGHT,
} from '@/lib/puck-element-layout-slots'
import { cn } from '@/lib/utils'

import {
  COLUMN_PATTERNS,
  ElementBadge,
  ElementBadgeList,
  ElementBlockquote,
  ElementButton,
  ElementCode,
  ElementContainer,
  ElementCustomHTML,
  ElementEmbed,
  ElementForm,
  ElementHeading,
  ElementImage,
  ElementLine,
  ElementLink,
  ElementList,
  ElementParagraph,
  ElementSection,
  ElementStack,
  ElementVideo,
} from '@/components/elements/element-components'

// ─── Layout Elements ─────────────────────────────────────────────────

const ElementSectionConfig: ComponentConfig = {
  label: 'Section',
  defaultProps: { bgColor: '', padding: 'py-16 px-4', className: '', [LAYOUT_CONTENT_SLOT]: [] },
  fields: {
    bgColor: { type: 'text', label: 'Background Color Class' },
    padding: { type: 'text', label: 'Padding Class' },
    className: { type: 'text', label: 'CSS Classes' },
    [LAYOUT_CONTENT_SLOT]: { type: 'slot' },
  },
  render: ({ bgColor, padding, className, content: Content, puck: _puck }) => (
    <ElementSection bgColor={bgColor} padding={padding} className={className}>
      <Content className="w-full" minEmptyHeight={LAYOUT_SLOT_MIN_HEIGHT} />
    </ElementSection>
  ),
}

const ElementContainerConfig: ComponentConfig = {
  label: 'Container',
  defaultProps: { maxWidth: 'max-w-7xl', padding: 'px-4', className: '', [LAYOUT_CONTENT_SLOT]: [] },
  fields: {
    maxWidth: {
      type: 'select',
      label: 'Max Width',
      options: [
        { label: 'Small (max-w-3xl)', value: 'max-w-3xl' },
        { label: 'Medium (max-w-5xl)', value: 'max-w-5xl' },
        { label: 'Large (max-w-7xl)', value: 'max-w-7xl' },
        { label: 'Full', value: 'max-w-full' },
      ],
    },
    padding: { type: 'text', label: 'Padding Class' },
    className: { type: 'text', label: 'CSS Classes' },
    [LAYOUT_CONTENT_SLOT]: { type: 'slot' },
  },
  render: ({ maxWidth, padding, className, content: Content, puck: _puck }) => (
    <ElementContainer maxWidth={maxWidth} padding={padding} className={className}>
      <Content className="w-full" minEmptyHeight={LAYOUT_SLOT_MIN_HEIGHT} />
    </ElementContainer>
  ),
}

const ElementStackConfig: ComponentConfig = {
  label: 'Stack',
  defaultProps: { gap: 'gap-4', align: 'stretch', className: '', [LAYOUT_CONTENT_SLOT]: [] },
  fields: {
    gap: {
      type: 'select',
      label: 'Gap',
      options: [
        { label: 'None', value: 'gap-0' },
        { label: 'Small (gap-2)', value: 'gap-2' },
        { label: 'Medium (gap-4)', value: 'gap-4' },
        { label: 'Large (gap-6)', value: 'gap-6' },
        { label: 'XL (gap-8)', value: 'gap-8' },
      ],
    },
    align: {
      type: 'select',
      label: 'Alignment',
      options: [
        { label: 'Start', value: 'start' },
        { label: 'Center', value: 'center' },
        { label: 'End', value: 'end' },
        { label: 'Stretch', value: 'stretch' },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
    [LAYOUT_CONTENT_SLOT]: { type: 'slot' },
  },
  render: ({ gap, align, className, content: Content, puck: _puck }) => (
    <ElementStack gap={gap} align={align} className={className}>
      <Content className="flex w-full flex-col" minEmptyHeight={LAYOUT_SLOT_MIN_HEIGHT} />
    </ElementStack>
  ),
}

const GRID_COLUMN_OPTIONS = [
  { label: '2 Columns', value: 2 },
  { label: '3 Columns', value: 3 },
  { label: '4 Columns', value: 4 },
  { label: '5 Columns', value: 5 },
  { label: '6 Columns', value: 6 },
] as const

const GRID_GAP_FIELD = {
  type: 'select' as const,
  label: 'Gap',
  options: [
    { label: 'None', value: 'gap-0' },
    { label: 'Small (gap-2)', value: 'gap-2' },
    { label: 'Medium (gap-4)', value: 'gap-4' },
    { label: 'Large (gap-6)', value: 'gap-6' },
    { label: 'XL (gap-8)', value: 'gap-8' },
  ],
}

const ElementGridConfig: ComponentConfig = {
  label: 'Grid',
  defaultProps: {
    columns: 3,
    gap: 'gap-6',
    className: '',
    ...gridSlotDefaultPropsMax(),
  },
  fields: {
    columns: {
      type: 'select',
      label: 'Columns',
      options: [...GRID_COLUMN_OPTIONS],
    },
    gap: GRID_GAP_FIELD,
    className: { type: 'text', label: 'CSS Classes' },
    ...gridSlotFields(6),
  },
  render: props => {
    const columns = Number(props.columns) || 3
    const { gap, className } = props
    const slotKeys = getGridSlotKeys(columns)

    return (
      <div
        className={cn('grid w-full', gap, className)}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {slotKeys.map(key => {
          const Slot = props[key as keyof typeof props] as React.ComponentType<{
            minEmptyHeight?: string
            collisionAxis?: 'x' | 'y' | 'dynamic'
          }>
          return (
            <div key={key} className="flex min-h-[8rem] flex-col">
              {Slot ? <Slot minEmptyHeight={LAYOUT_SLOT_MIN_HEIGHT} collisionAxis="y" /> : null}
            </div>
          )
        })}
      </div>
    )
  },
}

// ─── Column Elements ─────────────────────────────────────────────────

function createColumnConfig(label: string, pattern: string): ComponentConfig {
  const slotKeys = getColumnSlotKeys(pattern)

  return {
    label,
    defaultProps: { pattern, gap: 'gap-6', className: '', ...columnSlotDefaultProps(pattern) },
    fields: {
      gap: {
        type: 'select',
        label: 'Gap',
        options: [
          { label: 'None', value: 'gap-0' },
          { label: 'Small (gap-2)', value: 'gap-2' },
          { label: 'Medium (gap-4)', value: 'gap-4' },
          { label: 'Large (gap-6)', value: 'gap-6' },
          { label: 'XL (gap-8)', value: 'gap-8' },
        ],
      },
      className: { type: 'text', label: 'CSS Classes' },
      ...columnSlotFields(pattern),
    },
    render: props => {
      const { gap, className } = props
      const gridTemplate = COLUMN_PATTERNS[pattern] || COLUMN_PATTERNS['2']

      return (
        <div className={cn('grid w-full', gap, className)} style={{ gridTemplateColumns: gridTemplate }}>
          {slotKeys.map(key => {
            const Slot = props[key as keyof typeof props] as React.ComponentType<{
              minEmptyHeight?: string
            }>
            return Slot ? <Slot key={key} minEmptyHeight={LAYOUT_SLOT_MIN_HEIGHT} /> : null
          })}
        </div>
      )
    },
  }
}

// ─── Text Elements ───────────────────────────────────────────────────

const ElementHeadingConfig: ComponentConfig = {
  label: 'Heading',
  defaultProps: { text: 'Heading', level: 'h2', align: 'left', className: '' },
  fields: {
    text: { type: 'text', label: 'Text' },
    level: {
      type: 'select',
      label: 'Level',
      options: [
        { label: 'H1', value: 'h1' },
        { label: 'H2', value: 'h2' },
        { label: 'H3', value: 'h3' },
        { label: 'H4', value: 'h4' },
        { label: 'H5', value: 'h5' },
        { label: 'H6', value: 'h6' },
      ],
    },
    align: {
      type: 'select',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ text, level, align, className, puck: _puck }) => (
    <ElementHeading text={text} level={level} align={align} className={className} />
  ),
}

const ElementParagraphConfig: ComponentConfig = {
  label: 'Paragraph',
  defaultProps: { text: 'Enter your text here...', align: 'left', size: 'base', className: '' },
  fields: {
    text: { type: 'textarea', label: 'Text' },
    align: {
      type: 'select',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
    size: {
      type: 'select',
      label: 'Size',
      options: [
        { label: 'Small', value: 'sm' },
        { label: 'Normal', value: 'base' },
        { label: 'Large', value: 'lg' },
        { label: 'Extra Large', value: 'xl' },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ text, align, size, className, puck: _puck }) => (
    <ElementParagraph text={text} align={align} size={size} className={className} />
  ),
}

// ─── Action Elements ─────────────────────────────────────────────────

const ElementButtonConfig: ComponentConfig = {
  label: 'Button',
  defaultProps: { text: 'Click me', href: '#', variant: 'primary', size: 'md', fullWidth: false, className: '' },
  fields: {
    text: { type: 'text', label: 'Text' },
    href: { type: 'text', label: 'URL' },
    variant: {
      type: 'select',
      label: 'Variant',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
        { label: 'Outline', value: 'outline' },
      ],
    },
    size: {
      type: 'select',
      label: 'Size',
      options: [
        { label: 'Small', value: 'sm' },
        { label: 'Medium', value: 'md' },
        { label: 'Large', value: 'lg' },
      ],
    },
    fullWidth: {
      type: 'radio',
      label: 'Full Width',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ text, href, variant, size, fullWidth, className, puck: _puck }) => (
    <ElementButton text={text} href={href} variant={variant} size={size} fullWidth={fullWidth} className={className} />
  ),
}

const ElementFormConfig: ComponentConfig = {
  label: 'Form',
  defaultProps: {
    heading: 'Contact Form',
    showName: true,
    showEmail: true,
    showMessage: true,
    buttonText: 'Submit',
    className: '',
  },
  fields: {
    heading: { type: 'text', label: 'Heading' },
    showName: {
      type: 'radio',
      label: 'Show Name Field',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    showEmail: {
      type: 'radio',
      label: 'Show Email Field',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    showMessage: {
      type: 'radio',
      label: 'Show Message Field',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    buttonText: { type: 'text', label: 'Button Text' },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ heading, showName, showEmail, showMessage, buttonText, className, puck: _puck }) => (
    <ElementForm
      heading={heading}
      showName={showName}
      showEmail={showEmail}
      showMessage={showMessage}
      buttonText={buttonText}
      className={className}
    />
  ),
}

const ElementLinkConfig: ComponentConfig = {
  label: 'Link',
  defaultProps: { text: 'Link text', href: '#', newTab: false, className: '' },
  fields: {
    text: { type: 'text', label: 'Text' },
    href: { type: 'text', label: 'URL' },
    newTab: {
      type: 'radio',
      label: 'Open in New Tab',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ text, href, newTab, className, puck: _puck }) => (
    <ElementLink text={text} href={href} newTab={newTab} className={className} />
  ),
}

// ─── Media Elements ──────────────────────────────────────────────────

const ElementImageConfig: ComponentConfig = {
  label: 'Image',
  defaultProps: { src: '', alt: 'Image', objectFit: 'cover', rounded: 'md', className: '' },
  fields: {
    src: { type: 'text', label: 'Image URL' },
    alt: { type: 'text', label: 'Alt Text' },
    objectFit: {
      type: 'select',
      label: 'Object Fit',
      options: [
        { label: 'Cover', value: 'cover' },
        { label: 'Contain', value: 'contain' },
        { label: 'Fill', value: 'fill' },
        { label: 'None', value: 'none' },
      ],
    },
    rounded: {
      type: 'select',
      label: 'Border Radius',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Medium', value: 'md' },
        { label: 'Large', value: 'lg' },
        { label: 'XL', value: 'xl' },
        { label: 'Full', value: 'full' },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ src, alt, objectFit, rounded, className, puck: _puck }) => (
    <ElementImage src={src} alt={alt} objectFit={objectFit} rounded={rounded} className={className} />
  ),
}

const ElementVideoConfig: ComponentConfig = {
  label: 'Video',
  defaultProps: { src: '', poster: '', autoplay: false, controls: true, className: '' },
  fields: {
    src: { type: 'text', label: 'Video URL' },
    poster: { type: 'text', label: 'Poster Image URL' },
    autoplay: {
      type: 'radio',
      label: 'Autoplay',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    controls: {
      type: 'radio',
      label: 'Show Controls',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ src, poster, autoplay, controls, className, puck: _puck }) => (
    <ElementVideo src={src} poster={poster} autoplay={autoplay} controls={controls} className={className} />
  ),
}

const ElementEmbedConfig: ComponentConfig = {
  label: 'Embed',
  defaultProps: { url: '', height: 400, className: '' },
  fields: {
    url: { type: 'text', label: 'Embed URL' },
    height: { type: 'number', label: 'Height (px)', min: 100, max: 1200 },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ url, height, className, puck: _puck }) => <ElementEmbed url={url} height={height} className={className} />,
}

// ─── Basic Elements ──────────────────────────────────────────────────

const ElementBadgeConfig: ComponentConfig = {
  label: 'Badge',
  defaultProps: { text: 'Badge', color: 'blue', className: '' },
  fields: {
    text: { type: 'text', label: 'Text' },
    color: {
      type: 'select',
      label: 'Color',
      options: [
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
        { label: 'Red', value: 'red' },
        { label: 'Yellow', value: 'yellow' },
        { label: 'Purple', value: 'purple' },
        { label: 'Gray', value: 'gray' },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ text, color, className, puck: _puck }) => <ElementBadge text={text} color={color} className={className} />,
}

const ElementBadgeListConfig: ComponentConfig = {
  label: 'Badge List',
  defaultProps: {
    badges: [
      { text: 'Badge 1', color: 'blue' },
      { text: 'Badge 2', color: 'green' },
      { text: 'Badge 3', color: 'purple' },
    ],
    gap: 'gap-2',
    className: '',
  },
  fields: {
    badges: {
      type: 'array',
      label: 'Badges',
      arrayFields: {
        text: { type: 'text', label: 'Text' },
        color: {
          type: 'select',
          label: 'Color',
          options: [
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
            { label: 'Red', value: 'red' },
            { label: 'Yellow', value: 'yellow' },
            { label: 'Purple', value: 'purple' },
            { label: 'Gray', value: 'gray' },
          ],
        },
      },
      getItemSummary: (item: { text: string }) => item.text || 'Badge',
      defaultItemProps: { text: 'New Badge', color: 'blue' },
    },
    gap: {
      type: 'select',
      label: 'Gap',
      options: [
        { label: 'Small (gap-1)', value: 'gap-1' },
        { label: 'Medium (gap-2)', value: 'gap-2' },
        { label: 'Large (gap-3)', value: 'gap-3' },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ badges, gap, className, puck: _puck }) => (
    <ElementBadgeList badges={badges} gap={gap} className={className} />
  ),
}

const ElementBlockquoteConfig: ComponentConfig = {
  label: 'Blockquote',
  defaultProps: { text: 'This is a blockquote.', citation: '', className: '' },
  fields: {
    text: { type: 'textarea', label: 'Quote Text' },
    citation: { type: 'text', label: 'Citation' },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ text, citation, className, puck: _puck }) => (
    <ElementBlockquote text={text} citation={citation} className={className} />
  ),
}

const ElementCodeConfig: ComponentConfig = {
  label: 'Code',
  defaultProps: { code: 'console.log("Hello World")', language: 'javascript', className: '' },
  fields: {
    code: { type: 'textarea', label: 'Code' },
    language: { type: 'text', label: 'Language' },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ code, language, className, puck: _puck }) => (
    <ElementCode code={code} language={language} className={className} />
  ),
}

const ElementCustomHTMLConfig: ComponentConfig = {
  label: 'Custom HTML',
  defaultProps: { html: '<p>Custom HTML content</p>', className: '' },
  fields: {
    html: { type: 'textarea', label: 'HTML Content' },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ html, className, puck: _puck }) => <ElementCustomHTML html={html} className={className} />,
}

const ElementLineConfig: ComponentConfig = {
  label: 'Line',
  defaultProps: { className: '' },
  fields: {
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ className, puck: _puck }) => <ElementLine className={className} />,
}

const ElementListConfig: ComponentConfig = {
  label: 'List',
  defaultProps: {
    items: ['List item 1', 'List item 2', 'List item 3'],
    ordered: false,
    className: '',
  },
  fields: {
    items: {
      type: 'array',
      label: 'Items',
      arrayFields: {
        value: { type: 'text', label: 'Item Text' },
      },
      getItemSummary: (item: { value: string }) => item.value || 'Item',
      defaultItemProps: { value: 'New item' },
    },
    ordered: {
      type: 'radio',
      label: 'Ordered List',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    className: { type: 'text', label: 'CSS Classes' },
  },
  render: ({ items, ordered, className, puck: _puck }) => {
    // items from array field comes as { value: string }[] — extract string values
    const stringItems = Array.isArray(items)
      ? items.map((item: string | { value: string }) => (typeof item === 'string' ? item : item.value))
      : []
    return <ElementList items={stringItems} ordered={ordered} className={className} />
  },
}

// ─── Export All Element Configs ──────────────────────────────────────

export const elementConfigs: Record<string, ComponentConfig> = {
  // Layout
  ElementSection: ElementSectionConfig,
  ElementContainer: ElementContainerConfig,
  ElementStack: ElementStackConfig,
  ElementGrid: ElementGridConfig,
  // Columns
  ElementColumn1: createColumnConfig('Column 1', '1'),
  ElementColumn2: createColumnConfig('Column 2', '2'),
  ElementColumn3: createColumnConfig('Column 3', '3'),
  ElementColumn4: createColumnConfig('Column 4', '4'),
  ElementColumn2_1: createColumnConfig('Column 2:1', '2:1'),
  ElementColumn1_2: createColumnConfig('Column 1:2', '1:2'),
  ElementColumn1_2_1: createColumnConfig('Column 1:2:1', '1:2:1'),
  ElementColumn2_1_2: createColumnConfig('Column 2:1:2', '2:1:2'),
  // Text
  ElementHeading: ElementHeadingConfig,
  ElementParagraph: ElementParagraphConfig,
  // Actions
  ElementButton: ElementButtonConfig,
  ElementForm: ElementFormConfig,
  ElementLink: ElementLinkConfig,
  // Media
  ElementEmbed: ElementEmbedConfig,
  ElementImage: ElementImageConfig,
  ElementVideo: ElementVideoConfig,
  // Basic
  ElementBadge: ElementBadgeConfig,
  ElementBadgeList: ElementBadgeListConfig,
  ElementBlockquote: ElementBlockquoteConfig,
  ElementCode: ElementCodeConfig,
  ElementCustomHTML: ElementCustomHTMLConfig,
  ElementLine: ElementLineConfig,
  ElementList: ElementListConfig,
}

/**
 * Element category definition for the Elements tab.
 * Maps category IDs to their component keys and display info.
 */
export interface ElementCategoryDef {
  id: string
  title: string
  components: string[]
}

export const ELEMENT_CATEGORY_DEFS: ElementCategoryDef[] = [
  {
    id: 'layout',
    title: 'Layout',
    components: ['ElementSection', 'ElementContainer', 'ElementStack', 'ElementGrid'],
  },
  {
    id: 'column',
    title: 'Column',
    components: [
      'ElementColumn1',
      'ElementColumn2',
      'ElementColumn3',
      'ElementColumn4',
      'ElementColumn2_1',
      'ElementColumn1_2',
      'ElementColumn1_2_1',
      'ElementColumn2_1_2',
    ],
  },
  {
    id: 'text',
    title: 'Text',
    components: ['ElementParagraph', 'ElementHeading'],
  },
  {
    id: 'actions',
    title: 'Actions',
    components: ['ElementButton', 'ElementForm', 'ElementLink'],
  },
  {
    id: 'media',
    title: 'Media',
    components: ['ElementEmbed', 'ElementImage', 'ElementVideo'],
  },
  {
    id: 'basic',
    title: 'Basic',
    components: [
      'ElementBadge',
      'ElementBadgeList',
      'ElementBlockquote',
      'ElementCode',
      'ElementCustomHTML',
      'ElementLine',
      'ElementList',
    ],
  },
]
